import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import HeaderFour from "../../components/Header/HeaderFour";
import Footer from "../../components/Footer";
import Breadcrumb from "../../components/Breadcrumb";
import Seo from "../../components/Seo";
import makeRequest from "../../makeRequest";
import config from "../../config";

const STYLE_ID = "e-service-detail-styles";

const injectStyles = () => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    .esd-section {
      padding: 60px 0 80px;
      background: #f8f9fa;
    }
    .esd-card {
      background: #fff;
      border-radius: 16px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.09);
      overflow: hidden;
      max-width: 900px;
      margin: 0 auto;
    }
    .esd-hero-img {
      width: 100%;
      max-height: 360px;
      object-fit: cover;
    }
    .esd-hero-placeholder {
      width: 100%;
      height: 240px;
      background: linear-gradient(135deg, #e3f0ff 0%, #c9e2ff 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 3rem;
      color: #4a90d9;
    }
    .esd-body {
      padding: 36px 40px 40px;
    }
    .esd-title {
      font-size: 2rem;
      font-weight: 700;
      color: #1a2e4a;
      margin-bottom: 12px;
      line-height: 1.3;
    }
    .esd-description {
      font-size: 1.05rem;
      color: #6c757d;
      line-height: 1.7;
      margin-bottom: 28px;
    }
    .esd-html-content {
      color: #333;
      line-height: 1.8;
      font-size: 1rem;
    }
    .esd-html-content h1,
    .esd-html-content h2,
    .esd-html-content h3,
    .esd-html-content h4 {
      color: #1a2e4a;
      margin-top: 1.5em;
      margin-bottom: 0.6em;
      font-weight: 700;
    }
    .esd-html-content p { margin-bottom: 1em; }
    .esd-html-content ul,
    .esd-html-content ol {
      padding-left: 1.5em;
      margin-bottom: 1em;
    }
    .esd-html-content li { margin-bottom: 0.4em; }
    .esd-html-content a { color: #1a6fc4; text-decoration: underline; }
    .esd-html-content img { max-width: 100%; border-radius: 8px; margin: 12px 0; }
    .esd-html-content table {
      width: 100%;
      border-collapse: collapse;
      margin: 1em 0;
    }
    .esd-html-content th,
    .esd-html-content td {
      border: 1px solid #dee2e6;
      padding: 10px 14px;
      text-align: left;
    }
    .esd-html-content th { background: #f1f5f9; font-weight: 600; }
    .esd-footer-row {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-top: 36px;
      padding-top: 24px;
      border-top: 1px solid #e9ecef;
      flex-wrap: wrap;
    }
    .esd-btn {
      display: inline-block;
      padding: 12px 28px;
      background: #1a6fc4;
      color: #fff !important;
      border-radius: 8px;
      font-size: 0.95rem;
      font-weight: 600;
      text-decoration: none !important;
      transition: background 0.2s;
    }
    .esd-btn:hover {
      background: #145ea3;
      color: #fff !important;
    }
    .esd-back-link {
      color: #6c757d;
      font-size: 0.9rem;
      text-decoration: none;
    }
    .esd-back-link:hover { color: #1a6fc4; }
    .esd-loading,
    .esd-not-found {
      text-align: center;
      padding: 80px 20px;
      color: #6c757d;
      font-size: 1.1rem;
    }
    @media (max-width: 576px) {
      .esd-body { padding: 24px 20px; }
      .esd-title { font-size: 1.5rem; }
    }
  `;
  document.head.appendChild(style);
};

const EServiceDetail = () => {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [imgSrc, setImgSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    injectStyles();
  }, []);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await makeRequest({ route: `/website/e-service/${id}`, method: "get" });
        setItem(res?.data || null);
      } catch {
        setItem(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (!item?.hasImage) return;
    let objectUrl = null;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${config.api.baseUrl}/website/e-service/${id}/image`);
        if (!res.ok) return;
        const blob = await res.blob();
        if (!cancelled) {
          objectUrl = URL.createObjectURL(blob);
          setImgSrc(objectUrl);
        }
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id, item]);

  const isExternal = item?.buttonLink && (item.buttonLink.startsWith("http://") || item.buttonLink.startsWith("https://"));

  return (
    <>
      <Seo title={`${item?.title || item?.name || "E-Service"} | Maroof International Hospital`} />
      <HeaderFour />
      <Breadcrumb title={item?.title || item?.name || "E-Service"} />
      <section className="esd-section">
        <div className="container">
          {loading ? (
            <div className="esd-loading">Loading…</div>
          ) : !item ? (
            <div className="esd-not-found">
              E-Service not found. <Link to="/e-services">Back to E-Services</Link>
            </div>
          ) : (
            <div className="esd-card">
              {imgSrc ? (
                <img src={imgSrc} alt={item.title || item.name} className="esd-hero-img" />
              ) : (
                <div className="esd-hero-placeholder">
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="2" y="3" width="20" height="14" rx="2" />
                    <path d="M8 21h8M12 17v4" />
                  </svg>
                </div>
              )}
              <div className="esd-body">
                <h1 className="esd-title">{item.title || item.name}</h1>
                {item.description && <p className="esd-description">{item.description}</p>}
                {item.detailHtml && <div className="esd-html-content" dangerouslySetInnerHTML={{ __html: item.detailHtml }} />}
                <div className="esd-footer-row">
                  {item.buttonName &&
                    (isExternal ? (
                      <a href={item.buttonLink} target="_blank" rel="noopener noreferrer" className="esd-btn">
                        {item.buttonName}
                      </a>
                    ) : (
                      <Link to={item.buttonLink || "#"} className="esd-btn">
                        {item.buttonName}
                      </Link>
                    ))}
                  <Link to="/e-services" className="esd-back-link">
                    ← Back to E-Services
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
};

export default EServiceDetail;
